import _ from 'lodash';
import { getColumn, checkColumnDefined, checkCodeColumn } from '../utils';
import { checkRowNumRange, checkRowNumType } from '../utils';
import { TAG_TYPE_DROPDOWN_COLUMN, TAG_TYPE_MULTISELECT_COLUMN } from '../GridConstants';
import { uuid } from '../../../util';
import { CodeManager, CodeUtil } from '../../../code';

export default {
    effects: {
        *getCellContent({ payload }, RUI) {
            const { rowNumber, columnName } = payload;
            const { data, cols } = yield RUI.getState();
            const gridNamespace = yield RUI.getNamespace();

            // 入参合法性校验
            checkRowNumType(rowNumber, 'Grid.getCellContent(rowNum, columnName)');
            checkRowNumRange(rowNumber, data, 'Grid.getCellContent(rowNum, columnName)');
            checkColumnDefined(cols, columnName, 'Grid.getCellContent(rowNum, columnName)');
            checkCodeColumn(cols, columnName, 'Grid.getCellContent(rowNum, columnName)');

            // 取 col
            const col = getColumn(cols, columnName);
            // 判定 col 类型是否为 DropdownColumn 或 MultiSelectColumn
            const { TAG_TYPE } = col;

            // 取 cellValue
            const cellValue = yield RUI.invoke('getCellValue', {
                rowNumber: rowNumber,
                columnName: columnName
            });

            if (!cellValue) {
                return '';
            }

            // 取 codeMap
            let { codeMap, code, prefix } = col;
            if (codeMap == null && code) {
                codeMap = yield CodeManager.getCode(gridNamespace, code, prefix);
            }

            if (TAG_TYPE === TAG_TYPE_DROPDOWN_COLUMN) {
                // 查 content
                for (let i = 0; i < codeMap.length; i++) {
                    const { value, content } = codeMap[i];
                    if (cellValue === value) {
                        return content;
                    }
                }

                // 未查到
                return cellValue;
            } else if (TAG_TYPE === TAG_TYPE_MULTISELECT_COLUMN) {
                let contentResultValueArray = [];
                const cellValueArray = cellValue.split(',');

                for (let i = 0; i < cellValueArray.length; i++) {
                    const curValue = cellValueArray[i];

                    let index = codeMap.findIndex(({ value, content }) => {
                        return curValue === value;
                    });
                    if (index !== -1) {
                        contentResultValueArray.push(codeMap[index].content);
                    } else {
                        contentResultValueArray.push(curValue);
                    }
                }

                return contentResultValueArray.join(',');
            }
        },

        *setColumnCodeMap({ payload }, RUI) {
            const { cols, data } = yield RUI.getState();
            const { columnName, codeMap } = payload;
            const gridNamespace = yield RUI.getNamespace();

            // 校验 columnName 合法性
            checkColumnDefined(cols, columnName, `Grid.setColumnCodeMap(columnName, codeMap)`);
            checkCodeColumn(cols, columnName, 'Grid.setColumnCodeMap(columnName, codeMap)');

            // 取 col
            const col = getColumn(cols, columnName);
            // 取 codeMap
            let { codeMap: stateCodeMap, code, prefix } = col;
            if (stateCodeMap == null && code) {
                yield CodeManager.wait(gridNamespace);
            }

            if (!codeMap || !(codeMap instanceof Array)) {
                throw new Error(
                    `Grid.setColumnCodeMap(columnName, codeMap) 入参 codeMap 必须为非空数组，当前传入【${codeMap}:${typeof codeMap}】，请检查！`
                );
            }

            // 校验 codeMap 合法性
            if (codeMap.length) {
                let keys = Object.keys(codeMap[0]);
                if (keys.indexOf('value') === -1 || keys.indexOf('content') === -1) {
                    throw new Error(
                        `Grid.setColumnCodeMap(columnName, codeMap) 入参 codeMap 必须符合 [{value:v1,content:c1},{value:v2,content:c2}...结构]，当前传入【${JSON.stringify(
                            codeMap
                        )}】请检查！`
                    );
                }
            }

            const colName = columnName.toLowerCase();
            // 更新 codeMap
            yield RUI.invoke('setColumnCodeMapSuccess', [
                {
                    name: colName,
                    codeMap
                }
            ]);

            //重置当前列的值
            let resultDs = [];
            for (let i = 0; i < data.length; i++) {
                resultDs[i] = {
                    ...data[i],
                    [colName]: ''
                };
            }

            yield RUI.invoke('fillDataSuccess', {
                data: resultDs
            });
        },

        *getColumnCodeMap({ payload }, RUI) {
            const { cols } = yield RUI.getState();
            const { columnName } = payload;
            const gridNamespace = yield RUI.getNamespace();

            // 校验 columnName 合法性
            checkColumnDefined(cols, columnName, `Grid.getColumnCodeMap(columnName)`);
            checkCodeColumn(cols, columnName, 'Grid.getColumnCodeMap(columnName)');

            // 取 col
            const col = getColumn(cols, columnName);

            // 取 codeMap
            let { codeMap: stateCodeMap, code, prefix } = col;
            if (stateCodeMap == null && code) {
                let codeMap = yield CodeManager.getCode(gridNamespace, code, prefix);
                return codeMap;
            }

            // 取 codeMap
            const { codeMap } = col;

            // deepClone
            return _.cloneDeep(codeMap);
        },

        *updateColumnsCodeMap({ payload }, RUI) {
            const namespace = yield RUI.getNamespace();
            let promiseKey = uuid();
            CodeManager.startOne(namespace, promiseKey);
            const codeInfoArray = payload;

            const codeMapResult = yield CodeManager.getCodeList(namespace, codeInfoArray);

            yield RUI.invoke('updateColumnsCodeMapSuccess', codeMapResult);

            //addOne和completeOne的作用，为了防止codeMap
            CodeManager.completeOne(namespace, promiseKey);
        }
    },

    reducers: {
        updateColumnsCodeMapSuccess(state, { payload }) {
            const codeMapResult = payload;

            return {
                ...state,
                cols: state.cols.map((col) => {
                    let { code, prefix, excludeCodes } = col;
                    let newCodeMap = [];
                    if (code && codeMapResult[code.toUpperCase()]) {
                        newCodeMap = CodeUtil.fiterCodeMapByPrefix(codeMapResult[code.toUpperCase()], prefix);
                        newCodeMap = CodeUtil.filterCodeMapByExcludeCodes(newCodeMap, excludeCodes);

                        return {
                            ...col,
                            codeMap: newCodeMap
                        };
                    } else {
                        return col;
                    }
                })
            };
        },

        setColumnCodeMapSuccess(state, { payload }) {
            const codeMapArr = payload;

            return {
                ...state,
                cols: state.cols.map((col) => {
                    const newMap = codeMapArr.find(({ name }) => name == col.name);
                    if (newMap) {
                        return {
                            ...col,
                            codeMap: newMap.codeMap
                        };
                    } else {
                        return col;
                    }
                })
            };
        }
    }
};
